import { HOTSPOTS, INFOMODULES, VEILIGHEIDSUITDAGINGEN, type Video } from '../content/content'
import { ZWEEFFILM } from './zweeffilm'

/*
 * Offline op de beurs (PLAN.md §0.11, docs/video-r2.md).
 *
 * De films staan in R2 en komen normaal per knipje over het netwerk. Op een
 * beursvloer met trage wifi hapert dat. De knop "Alles offline laden" in het
 * beheerderspaneel haalt daarom elke film één keer in zijn geheel op en zet hem
 * in de cache van de service worker (public/sw.js). Daarna speelt de kiosk de
 * films van de eigen schijf, ook als het netwerk helemaal wegvalt.
 *
 * Dezelfde knop bewaart de applicatie zelf (de lijst uit dist/precache.json,
 * die de build schrijft), zodat de kiosk zonder netwerk ook opstart.
 *
 * De cachenamen moeten gelijk zijn aan die in public/sw.js.
 */
export const FILMS = 'motrac-films-v1'
export const APP = 'motrac-app-v1'

/** Kop die de service worker laat passeren: de laadknop wil het netwerk. */
const LADEN = { 'x-kiosk-laden': '1' }

const LAATST = 'motrac-kiosk-offline-geladen'

/** Elk bestand dat bij een film hoort: de film en zijn ondertitels. */
function filmBestanden(video: Video): string[] {
  return [video.src, video.ondertitels.nl, video.ondertitels.en].filter((s): s is string => !!s)
}

/** Alle films van de kiosk, zonder dubbele. */
export function alleFilms(): string[] {
  const films = [
    ...HOTSPOTS.map((h) => h.video),
    ...VEILIGHEIDSUITDAGINGEN.map((v) => v.video),
    // Stellingen en Tennant: alleen als er een film is. Ontbreekt een film
    // hier, dan wordt hij niet offline geladen én ruimt de lader hem op.
    ...Object.values(INFOMODULES).flatMap((m) => (m.video ? [m.video] : [])),
  ]
  return [...new Set(films.flatMap(filmBestanden))]
}

export const kanOffline = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'caches' in window

/**
 * Registreert de service worker. Alleen in een productiebuild: in `vite dev`
 * zou hij de modules van de ontwikkelserver vasthouden.
 */
export function registreer() {
  if (!import.meta.env.PROD || !kanOffline()) return
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .catch((fout) => console.warn('[offline] service worker niet geregistreerd', fout))
  })
}

/** Een bytegetal leesbaar: "3,4 MB", "512 MB". */
export const mb = (bytes: number) => {
  const m = bytes / 1_000_000
  return `${m < 10 ? m.toFixed(1).replace('.', ',') : Math.round(m)} MB`
}

export interface Voortgang {
  /** Wat nu binnenkomt: een film, of "applicatie". */
  bestand: string
  /** Stap van het totaal: één per film, en één voor de hele applicatie. */
  stap: number
  stappen: number
  /** Hoe ver de huidige stap is, van 0 tot 1. */
  deel: number
  /** Toelichting bij de stap, zoals "34 MB van 98 MB". */
  detail: string
}

/**
 * Haalt één bestand op en schrijft het rechtstreeks in de cache, terwijl de
 * bytes meetellen voor de voortgangsbalk. Doorstromen in plaats van eerst in
 * het geheugen verzamelen: een film van honderd megabyte past op een zwakke
 * beurs-pc niet zomaar in het werkgeheugen van het tabblad.
 */
async function bewaar(cache: Cache, pad: string, opBytes: (bytes: number, grootte: number | null) => void) {
  const antwoord = await fetch(pad, { headers: LADEN, cache: 'reload' })
  if (antwoord.status === 404) throw new Ontbreekt(pad)
  if (!antwoord.ok || !antwoord.body || (antwoord.status !== 200 && !heelBestand(antwoord))) {
    throw new Error(`${pad}: ${antwoord.status}`)
  }
  // Bij gecomprimeerde bestanden (de bundel) telt content-length de gecomprimeerde
  // bytes en de lezer de uitgepakte; dan valt er niets te controleren.
  const gecomprimeerd = !!antwoord.headers.get('content-encoding')
  const lengte = gecomprimeerd ? null : Number(antwoord.headers.get('content-length')) || null
  const type = antwoord.headers.get('content-type') ?? 'application/octet-stream'

  let bytes = 0
  const teller = new TransformStream<Uint8Array, Uint8Array>({
    transform(deel, uit) {
      bytes += deel.byteLength
      opBytes(bytes, lengte)
      uit.enqueue(deel)
    },
    flush() {
      // Een afgebroken verbinding mag geen halve film als complete achterlaten:
      // een fout hier laat cache.put mislukken en de oude kopie staan.
      if (lengte !== null && bytes !== lengte) throw new Error(`${pad}: ${bytes} van ${lengte} bytes`)
    },
  })
  // Opnieuw verpakken zonder de kopjes van de server (cache-control no-store,
  // etag): alleen het type en de lengte zijn hier van belang.
  const kopjes: Record<string, string> = { 'content-type': type }
  if (lengte !== null) kopjes['content-length'] = String(lengte)
  await cache.put(pad, new Response(antwoord.body.pipeThrough(teller), { headers: kopjes }))
}

/**
 * Een 206 die toch het hele bestand is (`bytes 0-n/n+1`). De Worker gaf die
 * tot 22 oktober op elke film uit R2; een kiosk die nog met een oude Worker
 * praat, of een tussenstation dat het zo doet, laadt dan toch.
 */
function heelBestand(antwoord: Response) {
  const m = /^bytes 0-(\d+)\/(\d+)$/.exec(antwoord.headers.get('content-range') ?? '')
  return antwoord.status === 206 && !!m && Number(m[1]) + 1 === Number(m[2])
}

/** Het bestand staat niet op de server (404). */
class Ontbreekt extends Error {}

/** De bestanden van de applicatie, zoals de build ze in precache.json zette. */
async function appBestanden(): Promise<string[]> {
  try {
    const antwoord = await fetch('/precache.json', { headers: LADEN, cache: 'reload' })
    if (!antwoord.ok) return []
    const lijst = (await antwoord.json()) as unknown
    return Array.isArray(lijst) ? lijst.filter((p): p is string => typeof p === 'string') : []
  } catch {
    return []
  }
}

/**
 * Laadt alle films en de applicatie in de cache. Een film die mislukt breekt
 * de rest niet af; de lijst met fouten komt terug. Een bestaande kopie wordt
 * pas vervangen als de nieuwe compleet binnen is.
 */
export async function laadAlles(opVoortgang: (v: Voortgang) => void): Promise<{ fouten: string[] }> {
  if (!kanOffline()) return { fouten: ['Deze browser kan niet offline bewaren.'] }
  // Vraag de browser de cache niet op te ruimen als de schijf vol raakt.
  await navigator.storage?.persist?.().catch(() => false)

  // De film van de zweefronde hoort erbij zodra hij er is (docs/zweeffilm.md).
  // Zolang hij niet in R2 staat is dat geen fout: het hoofdscherm is dan de hal.
  const films = [...alleFilms(), ZWEEFFILM]
  const app = await appBestanden()
  const stappen = films.length + (app.length > 0 ? 1 : 0)
  const fouten: string[] = []
  const [filmCache, appCache] = await Promise.all([caches.open(FILMS), caches.open(APP)])

  // Eerst de films, één voor één: ze zijn groot, en zo toont de balk per film
  // hoe ver hij is.
  for (const [stap, pad] of films.entries()) {
    opVoortgang({ bestand: pad, stap, stappen, deel: 0, detail: '' })
    try {
      await bewaar(filmCache, pad, (bytes, grootte) =>
        opVoortgang({
          bestand: pad, stap, stappen,
          deel: grootte ? bytes / grootte : 0,
          detail: grootte ? `${mb(bytes)} van ${mb(grootte)}` : mb(bytes),
        }),
      )
    } catch (fout) {
      if (pad === ZWEEFFILM && fout instanceof Ontbreekt) {
        await filmCache.delete(pad)
        continue
      }
      console.warn('[offline]', fout)
      fouten.push(pad)
    }
  }

  // Dan de applicatie: honderden kleine bestanden, een paar tegelijk, als
  // één stap op de balk.
  const rij = [...app]
  let binnen = 0
  const werker = async () => {
    for (let pad = rij.shift(); pad; pad = rij.shift()) {
      const dit = pad
      try {
        await bewaar(appCache, dit, () => {})
      } catch (fout) {
        console.warn('[offline]', fout)
        fouten.push(dit)
      }
      binnen++
      opVoortgang({
        bestand: 'applicatie', stap: films.length, stappen,
        deel: binnen / app.length, detail: `${binnen} van ${app.length} bestanden`,
      })
    }
  }
  await Promise.all(Array.from({ length: 6 }, werker))
  opVoortgang({ bestand: '', stap: stappen, stappen, deel: 0, detail: '' })

  // Oude bundelbestanden (een vorige deploy, een andere hash) opruimen, en
  // films die niet meer in de content staan.
  if (app.length > 0) {
    const houden = new Set(app)
    for (const verzoek of await appCache.keys()) {
      const pad = new URL(verzoek.url).pathname
      if (pad.startsWith('/assets/') && !houden.has(pad)) await appCache.delete(verzoek)
    }
  }
  const filmSet = new Set(films)
  for (const verzoek of await filmCache.keys()) {
    if (!filmSet.has(new URL(verzoek.url).pathname)) await filmCache.delete(verzoek)
  }

  if (fouten.length === 0) {
    try { localStorage.setItem(LAATST, new Date().toISOString()) } catch { /* privévenster */ }
  }
  return { fouten }
}

/** Gooit alles weg wat de laadknop bewaarde. */
export async function wisAlles() {
  if (!kanOffline()) return
  await Promise.all([caches.delete(FILMS), caches.delete(APP)])
  try { localStorage.removeItem(LAATST) } catch { /* privévenster */ }
}

export interface OfflineStatus {
  /** Films in de cache, van het totaal, en hun samen grootte in bytes. */
  films: number
  totaal: number
  bytes: number
  /** De film van de zweefronde in de cache, en zijn grootte; telt niet mee in `films`. */
  zweeffilm: number | null
  /** Wat de browser voor deze site gebruikt en toestaat. */
  gebruik: number | null
  ruimte: number | null
  /** Of de browser beloofde de cache niet op te ruimen. */
  blijvend: boolean
  /** Of de service worker de pagina bedient; zonder dat telt de cache niet. */
  actief: boolean
  /** Wanneer alles voor het laatst zonder fouten geladen is. */
  laatst: Date | null
}

export async function offlineStatus(): Promise<OfflineStatus | null> {
  if (!kanOffline()) return null
  const films = alleFilms().filter((p) => p.startsWith('/video/') && p.endsWith('.mp4'))
  const cache = await caches.open(FILMS)
  let aanwezig = 0
  let bytes = 0
  for (const pad of films) {
    const bewaard = await cache.match(pad)
    if (!bewaard) continue
    aanwezig++
    bytes += Number(bewaard.headers.get('content-length')) || 0
  }
  const zweef = await cache.match(ZWEEFFILM)
  const schatting = await navigator.storage?.estimate?.().catch(() => undefined)
  const blijvend = (await navigator.storage?.persisted?.().catch(() => false)) ?? false
  let laatst: Date | null = null
  try {
    const opgeslagen = localStorage.getItem(LAATST)
    if (opgeslagen) laatst = new Date(opgeslagen)
  } catch { /* privévenster */ }

  return {
    films: aanwezig,
    totaal: films.length,
    bytes,
    zweeffilm: zweef ? Number(zweef.headers.get('content-length')) || 0 : null,
    gebruik: schatting?.usage ?? null,
    ruimte: schatting?.quota ?? null,
    blijvend,
    actief: !!navigator.serviceWorker.controller,
    laatst,
  }
}
